import React from "react";
import { Box, Card, CardActionArea, Checkbox, Chip, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";

const formatDuration = (d) => (Number.isFinite(d) && d > 0 ? `${Math.round(d)}s` : null);
const fileNameOf = (url) => {
  if (!url) return "Raw video";
  try {
    return decodeURIComponent(url.split("/").pop().split("?")[0]);
  } catch {
    return url.split("/").pop().split("?")[0];
  }
};

/**
 * A WGID's raw clips as tickable cards, numbered in merge order — always the
 * order `rawVideos` is in (Admin Portal's), whatever order they were ticked.
 * Shared by the Step 3 card picker and the batch editor's video switch.
 */
export default function RawClipGrid({ rawVideos, selectedIds, onToggle }) {
  const ordered = rawVideos.filter((v) => selectedIds.includes(v.id));
  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
          gap: 1,
          p: 1,
          borderRadius: 2,
          bgcolor: (t) => alpha(t.palette.primary.main, 0.03),
          border: "1px dashed",
          borderColor: (t) => alpha(t.palette.primary.main, 0.2),
        }}
      >
        {rawVideos.map((v) => {
          const selected = selectedIds.includes(v.id);
          const order = ordered.findIndex((x) => x.id === v.id) + 1;
          const dur = formatDuration(v.duration);
          const name = fileNameOf(v.url);
          return (
            <Card
              key={v.id}
              variant="outlined"
              sx={{
                position: "relative",
                overflow: "visible",
                borderColor: selected ? "primary.main" : "divider",
                borderWidth: selected ? 2 : 1,
                boxShadow: selected ? 2 : 0,
                transition: "border-color .15s, box-shadow .15s",
              }}
            >
              <CardActionArea onClick={() => onToggle(v.id)} sx={{ p: 1 }}>
                <Stack direction="row" alignItems="flex-start" spacing={0.25}>
                  <Checkbox size="small" checked={selected} sx={{ p: 0.25, mt: -0.25 }} inputProps={{ "aria-label": name }} />
                  <Box flex={1} minWidth={0}>
                    <Typography variant="caption" fontWeight={600} noWrap title={name} display="block">
                      {name}
                    </Typography>
                    {dur && <Chip label={dur} size="small" sx={{ height: 16, fontSize: 10, mt: 0.5 }} />}
                  </Box>
                </Stack>
              </CardActionArea>
              {selected && (
                <Chip
                  label={order}
                  size="small"
                  color="primary"
                  sx={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    height: 20,
                    minWidth: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    "& .MuiChip-label": { px: "6px" },
                  }}
                />
              )}
            </Card>
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
        {selectedIds.length > 0
          ? `${selectedIds.length} clip${selectedIds.length > 1 ? "s" : ""} will be merged back-to-back, in the numbered order.`
          : "Select one or more raw clips to merge into the editor."}
      </Typography>
    </>
  );
}
